import React from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';

import { Toggle } from '../Toggle';
import { PopupMenuItem } from './PopupMenu';

type Props = {
  item: PopupMenuItem;
  onClick?: (item?: PopupMenuItem, event?: React.UIEvent) => void;
  dataTestid?: string;
  className?: string;
};

/**
 * PopupMenuRow.
 */
const PopupMenuRow = (props: Props) => {
  const { item, onClick, dataTestid, className } = props;
  const intl = useIntl();

  return (
    <Button
      variant="transparent"
      disabled={item.disabled}
      as={(item.as ?? item?.toggle) ? 'div' : 'button'}
      aria-disabled={item.disabled}
      tabIndex={item.tabIndex ?? (item.toggle ? -1 : undefined)}
      onClick={(e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
        e.preventDefault();
        onClick?.(item, e);
      }}
      onKeyDown={(
        e: React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>,
      ) => {
        if ([' ', 'Enter'].includes(e.key)) onClick?.(item, e);
      }}
      data-testid={dataTestid}
      className={`flex w-full justify-start gap-3 ${className}`}
    >
      <Flex className="items-center gap-3">
        {item.icon && (
          <Box className="basis-[22px]">
            <Icon className={item.iconColor} icon={item.icon} />
          </Box>
        )}
        <P
          className={`whitespace-break-spaces sm:whitespace-nowrap ${item.textColor}`}
        >
          {item.text}
        </P>
      </Flex>
      {item?.toggle && (
        <Flex className="ml-auto items-center">
          <Toggle
            buttonProps={{
              'aria-label':
                intl.formatMessage({
                  id: 'popup-item-toggle',
                  defaultMessage: 'Toggle',
                }) +
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                ' ' +
                item.text,
            }}
            size={14}
            checked={item?.checked}
            onChange={() => {
              onClick?.(item);
            }}
            data-testid={`popup-item-${item.id}-toggle`}
            aria-label={item.text}
          />
        </Flex>
      )}
    </Button>
  );
};

export default PopupMenuRow;
